// Pure helpers for the contact / bug report dialog (src/components/ContactDialog.js).
// FEEDBACK_MIN, FEEDBACK_MAX and FEEDBACK_TYPES must match the "feedback" schema in the backend's validation-schemas.js.
export const FEEDBACK_MIN = 10;
export const FEEDBACK_MAX = 5000;
export const FEEDBACK_TYPES = ["bug", "question", "other"];
// Longest browser identification string sent; must match USER_AGENT_MAX in the backend's feedback-handler.js, which
// truncates longer values as well (a long user agent never makes a report fail)
export const FEEDBACK_USER_AGENT_MAX = 500;

// One global window event, like the other ecoready:* events, so the footer, the sidebar and the 404 page
// can open the single dialog that index.js mounts.
export const CONTACT_EVENT = "ecoready:open-contact";

export const openContactDialog = () => {
	window.dispatchEvent(new CustomEvent(CONTACT_EVENT));
};

/** Registers a listener for openContactDialog(). Returns the function that removes it. */
export const onContactRequest = (handler) => {
	window.addEventListener(CONTACT_EVENT, handler);
	return () => window.removeEventListener(CONTACT_EVENT, handler);
};

/**
 * The page address to attach to a report: origin + path only. The query string and fragment can hold secrets
 * (Protected.js accepts ?token=<JWT>, and session tokens never expire), so they are never shown or sent.
 * Relative or unparsable values are cut at the first "?" or "#".
 */
export const pageUrlForReport = (href) => {
	const text = String(href ?? "");
	try {
		const { origin, pathname } = new URL(text);
		// "null" origin (data:, file:, ...) cannot be rebuilt safely; fall through to the plain cut
		if (origin !== "null") return `${origin}${pathname}`;
	} catch {
		// Not an absolute URL: handled below
	}

	return text.split(/[#?]/, 1)[0];
};

/** Returns the human-readable page name shown in the contact report. */
export const pageNameForReport = (href) => {
	const pageUrl = pageUrlForReport(href);
	let path;
	try {
		path = new URL(pageUrl).pathname;
	} catch {
		path = pageUrl;
	}

	const page = path.split("/").filter(Boolean)[0]?.replaceAll("-", " ").replaceAll("_", " ");
	if (!page) return "Home";
	return page.split(" ").filter(Boolean).map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`).join(" ");
};

/** @returns {"tooShort" | "tooLong" | null} the error key for the message, null when it is valid. */
export const validateFeedbackMessage = (message) => {
	const length = typeof message === "string" ? message.trim().length : 0;
	if (length < FEEDBACK_MIN) return "tooShort";
	if (length > FEEDBACK_MAX) return "tooLong";
	return null;
};

// The reply-to address is the signed-in user's, set by the server; it is deliberately not part of the payload.
export const buildFeedbackPayload = ({ type, message, page, userAgent }) => ({
	type,
	message: message.trim(),
	page,
	userAgent: typeof userAgent === "string" ? userAgent.slice(0, FEEDBACK_USER_AGENT_MAX) : userAgent,
});

/**
 * Classifies the outcome of sendFeedback.
 * @param {object} [result] - resolved response body
 * @param {Error} [error] - rejected error (ky HTTPError carries `response.status`)
 * @returns {"validation" | "rateLimit" | "generic" | null} null when the report was accepted
 */
export const feedbackErrorKey = (result, error) => {
	if (error) return error?.response?.status === 429 ? "rateLimit" : "generic";
	// The backend answers validation failures with HTTP 200 and { status: 400, message }
	if (result?.status === 400) return "validation";
	return result?.success === true ? null : "generic";
};
